/*
  CLAYIT — COSMOS
  One continuous journey: void → Earth → Moon → the book → Mars → Jupiter →
  Saturn → nebula → deep space → the pale blue dot → home.

  Architecture
  ────────────
  · Scroll is the camera dolly. A single damped progress value (0..1) drives a
    Catmull-Rom camera rail, a look-target rail, fov, exposure and every DOM
    chapter envelope. Nothing snaps; everything is a function of p.
  · No animation libraries. rAF + critically-damped smoothing.
  · Custom shaders: Earth (day/night terminator, cloud shadows, ocean specular,
    twilight ring), volumetric atmosphere shell, cloud layer, generic planet
    (soft terminator + haze), Jupiter differential band flow, Saturn rings,
    twinkling starfield, FBM nebula, sun glow, dust, shooting stars.
  · Post: UnrealBloom → Output → film pass (grain, vignette, edge CA).
  · Adaptive quality: DPR tiers step down if frame time degrades. Mobile gets
    a lighter particle budget. prefers-reduced-motion → direct scrub, no drift.
*/

import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';

const CFG = window.COSMOS_CONFIG || {};
const TEX = CFG.textures || {};
const isMobile = matchMedia('(max-width: 749px)').matches || /Mobi|Android/i.test(navigator.userAgent);
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
const finePointer = matchMedia('(pointer: fine)').matches;

/* ───────────────────────────── helpers ───────────────────────────── */

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const lerp = (a, b, t) => a + (b - a) * t;
const smoothstep = (a, b, x) => {
  const t = clamp((x - a) / (b - a), 0, 1);
  return t * t * (3 - 2 * t);
};
/* fade envelope for a chapter range */
const envelope = (p, a, b, wIn = 0.22, wOut = 0.22) => {
  const span = b - a;
  return smoothstep(a, a + span * wIn, p) * (1 - smoothstep(b - span * wOut, b, p));
};

/* ───────────────────────────── boot guard ───────────────────────────── */

const root = document.getElementById('cosmos');
if (!root) throw new Error('cosmos root missing');
const canvas = root.querySelector('.cosmos__canvas');

let renderer;
try {
  renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: false,
    powerPreference: 'high-performance',
    alpha: false,
  });
} catch (e) {
  root.classList.add('cosmos--fallback');
  throw e;
}
if (!renderer.getContext()) {
  root.classList.add('cosmos--fallback');
  throw new Error('no webgl');
}

renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.0;

/* ───────────────────────────── scene ───────────────────────────── */

const scene = new THREE.Scene();
scene.background = new THREE.Color(0x000005);
const camera = new THREE.PerspectiveCamera(60, innerWidth / innerHeight, 0.1, 14000);
camera.position.set(0, 2, 120);

const SUN_POS = new THREE.Vector3(-5200, 1500, 2600);
const sunDir = SUN_POS.clone().normalize();

const sunLight = new THREE.DirectionalLight(0xfff4e6, 3.2);
sunLight.position.copy(SUN_POS);
scene.add(sunLight);
scene.add(new THREE.AmbientLight(0x223355, 0.25));

/* ───────────────────────────── loading ───────────────────────────── */

const manager = new THREE.LoadingManager();
const loader = new THREE.TextureLoader(manager);
const preloadEl = document.querySelector('.cosmos-preload');
const preloadBar = document.querySelector('.cosmos-preload__bar i');
const preloadPct = document.querySelector('.cosmos-preload__pct');

manager.onProgress = (_, done, total) => {
  const f = total ? done / total : 1;
  if (preloadBar) preloadBar.style.transform = `scaleX(${f})`;
  if (preloadPct) preloadPct.textContent = String(Math.round(f * 100)).padStart(3, '0');
};

const texture = (url, { srgb = true, aniso = 4 } = {}) => {
  const t = loader.load(url);
  if (srgb) t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = aniso;
  return t;
};

const T = {
  earthDay: texture(TEX.earthDay, { aniso: 8 }),
  earthNight: texture(TEX.earthNight),
  earthClouds: texture(TEX.earthClouds, { srgb: false }),
  earthNormal: texture(TEX.earthNormal, { srgb: false }),
  earthSpec: texture(TEX.earthSpec, { srgb: false }),
  moon: texture(TEX.moon),
  mars: texture(TEX.mars),
  jupiter: texture(TEX.jupiter, { aniso: 8 }),
  saturn: texture(TEX.saturn),
  saturnRing: texture(TEX.saturnRing),
  venus: texture(TEX.venus),
  mercury: texture(TEX.mercury),
  uranus: texture(TEX.uranus),
  neptune: texture(TEX.neptune),
  milkyway: texture(TEX.milkyway),
};
T.earthClouds.wrapS = THREE.RepeatWrapping;

/* ───────────────────────────── starfield ───────────────────────────── */

function makeStars(count, radiusMin, radiusMax) {
  const pos = new Float32Array(count * 3);
  const col = new Float32Array(count * 3);
  const attr = new Float32Array(count * 3); // size, phase, speed
  const c = new THREE.Color();
  for (let i = 0; i < count; i++) {
    const r = lerp(radiusMin, radiusMax, Math.cbrt(Math.random()));
    const th = Math.random() * Math.PI * 2;
    const ph = Math.acos(2 * Math.random() - 1);
    pos[i * 3] = r * Math.sin(ph) * Math.cos(th);
    pos[i * 3 + 1] = r * Math.cos(ph);
    pos[i * 3 + 2] = r * Math.sin(ph) * Math.sin(th);
    const k = Math.random();
    if (k < 0.72) c.setHSL(0.62, 0.1, lerp(0.55, 0.95, Math.random()));
    else if (k < 0.9) c.setHSL(0.08, 0.28, lerp(0.6, 0.9, Math.random()));
    else c.setHSL(0.6, 0.35, 0.85);
    col[i * 3] = c.r; col[i * 3 + 1] = c.g; col[i * 3 + 2] = c.b;
    attr[i * 3] = lerp(1.3, 3.4, Math.pow(Math.random(), 3));
    attr[i * 3 + 1] = Math.random() * Math.PI * 2;
    attr[i * 3 + 2] = lerp(0.2, 1.6, Math.random());
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  g.setAttribute('aStar', new THREE.BufferAttribute(attr, 3));
  const m = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    uniforms: { uTime: { value: 0 }, uFade: { value: 0 }, uPx: { value: 1 } },
    vertexShader: /* glsl */`
      attribute vec3 aStar; attribute vec3 color;
      varying vec3 vC; varying float vTw;
      uniform float uTime, uPx;
      void main(){
        vC = color;
        vTw = 0.72 + 0.28 * sin(uTime * aStar.z + aStar.y);
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = aStar.x * uPx * (900.0 / -mv.z);
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: /* glsl */`
      varying vec3 vC; varying float vTw;
      uniform float uFade;
      void main(){
        float d = length(gl_PointCoord - 0.5);
        float a = smoothstep(0.5, 0.08, d);
        gl_FragColor = vec4(vC, a * vTw * uFade);
      }`,
  });
  return new THREE.Points(g, m);
}
const stars = makeStars(isMobile ? 7000 : 16000, 900, 8200);
scene.add(stars);

/* faint galaxy backdrop */
const milky = new THREE.Mesh(
  new THREE.SphereGeometry(9500, 48, 32),
  new THREE.MeshBasicMaterial({ map: T.milkyway, side: THREE.BackSide, transparent: true, opacity: 0.22, depthWrite: false })
);
milky.rotation.set(0.55, 2.2, 0.35);
milky.renderOrder = -2;
scene.add(milky);

/* ───────────────────────────── EARTH ───────────────────────────── */

const EARTH_R = 10;
const earthGroup = new THREE.Group();
scene.add(earthGroup);

const earthMat = new THREE.ShaderMaterial({
  uniforms: {
    uDay: { value: T.earthDay },
    uNight: { value: T.earthNight },
    uClouds: { value: T.earthClouds },
    uNormal: { value: T.earthNormal },
    uSpec: { value: T.earthSpec },
    uSunDir: { value: sunDir },
    uCloudShift: { value: 0 },
    uCamPos: { value: camera.position },
  },
  vertexShader: /* glsl */`
    varying vec2 vUv; varying vec3 vN; varying vec3 vWp;
    void main(){
      vUv = uv;
      vN = normalize(mat3(modelMatrix) * normal);
      vec4 wp = modelMatrix * vec4(position, 1.0);
      vWp = wp.xyz;
      gl_Position = projectionMatrix * viewMatrix * wp;
    }`,
  fragmentShader: /* glsl */`
    uniform sampler2D uDay, uNight, uClouds, uNormal, uSpec;
    uniform vec3 uSunDir, uCamPos;
    uniform float uCloudShift;
    varying vec2 vUv; varying vec3 vN; varying vec3 vWp;
    void main(){
      vec3 N = normalize(vN);
      vec3 Tn = normalize(cross(vec3(0.0, 1.0, 0.0), N));
      vec3 Bn = cross(N, Tn);
      vec3 nm = texture2D(uNormal, vUv).xyz * 2.0 - 1.0;
      N = normalize(Tn * nm.x * 0.55 + Bn * nm.y * 0.55 + N);
      vec3 V = normalize(uCamPos - vWp);
      float ndl = dot(N, uSunDir);
      float dayAmt = smoothstep(-0.10, 0.22, ndl);

      vec3 day = texture2D(uDay, vUv).rgb;
      float cloudShadow = texture2D(uClouds, vec2(vUv.x + uCloudShift, vUv.y)).r;
      day *= 1.0 - cloudShadow * 0.42;
      vec3 lit = day * (0.03 + 1.35 * max(ndl, 0.0));

      float twilight = smoothstep(0.22, 0.0, abs(ndl));
      lit += day * vec3(1.0, 0.42, 0.18) * twilight * 0.55;

      float ocean = texture2D(uSpec, vUv).r;
      vec3 H = normalize(uSunDir + V);
      float spec = pow(max(dot(N, H), 0.0), 48.0) * ocean;
      lit += vec3(0.9, 0.95, 1.0) * spec * dayAmt * 0.85;

      vec3 night = texture2D(uNight, vUv).rgb;
      vec3 cityGlow = night * vec3(1.0, 0.80, 0.55) * 2.1;
      float nightAmt = smoothstep(0.10, -0.20, ndl);

      vec3 col = lit * dayAmt + cityGlow * nightAmt * (1.0 - cloudShadow * 0.75);

      float fres = pow(1.0 - max(dot(normalize(vN), V), 0.0), 3.2);
      col += vec3(0.22, 0.46, 1.0) * fres * (0.18 + 0.85 * dayAmt);

      gl_FragColor = vec4(col, 1.0);
    }`,
});
const earth = new THREE.Mesh(new THREE.SphereGeometry(EARTH_R, 128, 96), earthMat);
earthGroup.add(earth);
earthGroup.rotation.z = THREE.MathUtils.degToRad(-23.4);

/* clouds */
const cloudMat = new THREE.ShaderMaterial({
  transparent: true, depthWrite: false,
  uniforms: { uClouds: { value: T.earthClouds }, uSunDir: { value: sunDir } },
  vertexShader: /* glsl */`
    varying vec2 vUv; varying vec3 vN;
    void main(){
      vUv = uv;
      vN = normalize(mat3(modelMatrix) * normal);
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }`,
  fragmentShader: /* glsl */`
    uniform sampler2D uClouds; uniform vec3 uSunDir;
    varying vec2 vUv; varying vec3 vN;
    void main(){
      float c = texture2D(uClouds, vUv).r;
      float ndl = dot(normalize(vN), uSunDir);
      float light = 0.10 + 1.15 * max(ndl, 0.0);
      float twilight = smoothstep(0.2, 0.0, abs(ndl));
      vec3 col = vec3(light) + vec3(1.0, 0.45, 0.25) * twilight * 0.35;
      gl_FragColor = vec4(col, c * (0.28 + 0.72 * smoothstep(-0.25, 0.15, ndl)));
    }`,
});
const clouds = new THREE.Mesh(new THREE.SphereGeometry(EARTH_R * 1.012, 96, 72), cloudMat);
earthGroup.add(clouds);

/* atmosphere shell */
function makeAtmosphere(radius, color, c = 0.72, pwr = 4.2, scale = 1.16) {
  const m = new THREE.ShaderMaterial({
    side: THREE.BackSide, transparent: true, depthWrite: false,
    blending: THREE.AdditiveBlending,
    uniforms: {
      uColor: { value: new THREE.Color(color) },
      uSunDir: { value: sunDir },
      uC: { value: c }, uP: { value: pwr }, uGain: { value: 1 },
    },
    vertexShader: /* glsl */`
      varying vec3 vVn; varying vec3 vWn;
      void main(){
        vVn = normalize(normalMatrix * normal);
        vWn = normalize(mat3(modelMatrix) * normal);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: /* glsl */`
      uniform vec3 uColor; uniform vec3 uSunDir;
      uniform float uC, uP, uGain;
      varying vec3 vVn; varying vec3 vWn;
      void main(){
        float i = pow(max(uC - dot(vVn, vec3(0.0, 0.0, 1.0)), 0.0), uP);
        float sun = 0.22 + 0.95 * smoothstep(-0.4, 0.55, dot(normalize(vWn), uSunDir));
        gl_FragColor = vec4(uColor * i * sun * uGain, i);
      }`,
  });
  return new THREE.Mesh(new THREE.SphereGeometry(radius * scale, 64, 48), m);
}
const atmo = makeAtmosphere(EARTH_R, 0x3d7dff);
earthGroup.add(atmo);

/* pale-blue-dot beacon for the finale */
const dotGlow = new THREE.Sprite(new THREE.SpriteMaterial({
  map: makeGlowTexture('#7db4ff'), transparent: true, opacity: 0,
  blending: THREE.AdditiveBlending, depthWrite: false,
}));
dotGlow.scale.setScalar(90);
scene.add(dotGlow);

function makeGlowTexture(color) {
  const s = 128, cv = document.createElement('canvas');
  cv.width = cv.height = s;
  const ctx = cv.getContext('2d');
  const g = ctx.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
  g.addColorStop(0, color);
  g.addColorStop(0.25, color + 'aa');
  g.addColorStop(1, 'transparent');
  ctx.fillStyle = g; ctx.fillRect(0, 0, s, s);
  const t = new THREE.CanvasTexture(cv);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/* ───────────────────────────── MOON ───────────────────────────── */

const MOON_ORBIT = 34;
const moonPivot = new THREE.Group();
scene.add(moonPivot);
const moon = new THREE.Mesh(
  new THREE.SphereGeometry(2.7, 64, 48),
  planetMaterial(T.moon, { atmColor: 0x9aa4b8, atmStrength: 0.06, ambient: 0.02 })
);
moon.position.set(MOON_ORBIT, 0, 0);
moonPivot.rotation.x = 0.09;
moonPivot.add(moon);

/* generic planet shader */
function planetMaterial(map, { atmColor = 0x88aaff, atmStrength = 0.25, ambient = 0.03, terminator = 0.22 } = {}) {
  return new THREE.ShaderMaterial({
    uniforms: {
      uMap: { value: map },
      uSunDir: { value: sunDir },
      uCamPos: { value: camera.position },
      uAtm: { value: new THREE.Color(atmColor) },
      uAtmS: { value: atmStrength },
      uAmb: { value: ambient },
      uTerm: { value: terminator },
    },
    vertexShader: /* glsl */`
      varying vec2 vUv; varying vec3 vN; varying vec3 vWp;
      void main(){
        vUv = uv;
        vN = normalize(mat3(modelMatrix) * normal);
        vec4 wp = modelMatrix * vec4(position, 1.0);
        vWp = wp.xyz;
        gl_Position = projectionMatrix * viewMatrix * wp;
      }`,
    fragmentShader: /* glsl */`
      uniform sampler2D uMap; uniform vec3 uSunDir, uCamPos, uAtm;
      uniform float uAtmS, uAmb, uTerm;
      varying vec2 vUv; varying vec3 vN; varying vec3 vWp;
      void main(){
        vec3 N = normalize(vN);
        vec3 V = normalize(uCamPos - vWp);
        float ndl = dot(N, uSunDir);
        float light = uAmb + max(ndl, 0.0) * 1.25;
        light *= smoothstep(-uTerm, uTerm, ndl) * 0.85 + 0.15;
        vec3 col = texture2D(uMap, vUv).rgb * light;
        float fres = pow(1.0 - max(dot(N, V), 0.0), 3.0);
        col += uAtm * fres * uAtmS * (0.25 + 0.9 * max(ndl, 0.0));
        gl_FragColor = vec4(col, 1.0);
      }`,
  });
}

/* ───────────────────────────── PLANETS ───────────────────────────── */

const planets = [];
function addPlanet(mesh, pos, spin = 0.01) {
  mesh.position.copy(pos);
  scene.add(mesh);
  planets.push({ mesh, spin });
  return mesh;
}

/* Venus, near the start of the road out */
addPlanet(new THREE.Mesh(
  new THREE.SphereGeometry(8.5, 72, 54),
  planetMaterial(T.venus, { atmColor: 0xffd9a0, atmStrength: 0.55, ambient: 0.05, terminator: 0.35 })
), new THREE.Vector3(-170, 34, -300), 0.004);

/* Mercury, a small hard stone far to the side */
addPlanet(new THREE.Mesh(
  new THREE.SphereGeometry(3.4, 56, 42),
  planetMaterial(T.mercury, { atmColor: 0x888888, atmStrength: 0.05, ambient: 0.02, terminator: 0.12 })
), new THREE.Vector3(210, -40, -520), 0.003);

/* Mars */
const mars = addPlanet(new THREE.Mesh(
  new THREE.SphereGeometry(6.2, 84, 64),
  planetMaterial(T.mars, { atmColor: 0xd9906a, atmStrength: 0.28, ambient: 0.03, terminator: 0.2 })
), new THREE.Vector3(95, -12, -430), 0.008);

/* Jupiter with animated differential band flow */
const jupiterMat = new THREE.ShaderMaterial({
  uniforms: {
    uMap: { value: T.jupiter },
    uSunDir: { value: sunDir },
    uCamPos: { value: camera.position },
    uTime: { value: 0 },
  },
  vertexShader: /* glsl */`
    varying vec2 vUv; varying vec3 vN; varying vec3 vWp;
    void main(){
      vUv = uv;
      vN = normalize(mat3(modelMatrix) * normal);
      vec4 wp = modelMatrix * vec4(position, 1.0);
      vWp = wp.xyz;
      gl_Position = projectionMatrix * viewMatrix * wp;
    }`,
  fragmentShader: /* glsl */`
    uniform sampler2D uMap; uniform vec3 uSunDir, uCamPos; uniform float uTime;
    varying vec2 vUv; varying vec3 vN; varying vec3 vWp;
    void main(){
      float band = sin(vUv.y * 28.0) * 0.5 + sin(vUv.y * 9.0 + 1.7) * 0.5;
      vec2 uv = vec2(fract(vUv.x + uTime * 0.0035 * band), vUv.y);
      uv.y += sin(uv.x * 40.0 + uTime * 0.12 + vUv.y * 30.0) * 0.0012;
      vec3 tex = texture2D(uMap, uv).rgb;
      vec3 N = normalize(vN);
      vec3 V = normalize(uCamPos - vWp);
      float ndl = dot(N, uSunDir);
      float light = 0.04 + max(ndl, 0.0) * 1.3;
      light *= smoothstep(-0.25, 0.25, ndl) * 0.85 + 0.15;
      vec3 col = tex * light;
      float fres = pow(1.0 - max(dot(N, V), 0.0), 3.0);
      col += vec3(0.9, 0.75, 0.55) * fres * 0.22 * (0.25 + 0.9 * max(ndl, 0.0));
      gl_FragColor = vec4(col, 1.0);
    }`,
});
const jupiter = addPlanet(new THREE.Mesh(new THREE.SphereGeometry(48, 112, 84), jupiterMat), new THREE.Vector3(-275, 26, -1080), 0.02);

/* Saturn + rings */
const saturnGroup = new THREE.Group();
saturnGroup.position.set(300, -18, -1780);
scene.add(saturnGroup);
const saturn = new THREE.Mesh(
  new THREE.SphereGeometry(40, 104, 78),
  planetMaterial(T.saturn, { atmColor: 0xf0dcae, atmStrength: 0.22, ambient: 0.035, terminator: 0.22 })
);
saturnGroup.add(saturn);
planets.push({ mesh: saturn, spin: 0.018 });

const ringGeo = new THREE.RingGeometry(48, 94, 220, 1);
{
  const pos = ringGeo.attributes.position;
  const uv = ringGeo.attributes.uv;
  const v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i);
    uv.setXY(i, (v.length() - 48) / (94 - 48), 0.5);
  }
}
const ringMat = new THREE.ShaderMaterial({
  side: THREE.DoubleSide, transparent: true, depthWrite: false,
  uniforms: { uMap: { value: T.saturnRing }, uSunDir: { value: sunDir } },
  vertexShader: /* glsl */`
    varying vec2 vUv; varying vec3 vWp;
    void main(){
      vUv = uv;
      vec4 wp = modelMatrix * vec4(position, 1.0);
      vWp = wp.xyz;
      gl_Position = projectionMatrix * viewMatrix * wp;
    }`,
  fragmentShader: /* glsl */`
    uniform sampler2D uMap; uniform vec3 uSunDir;
    varying vec2 vUv; varying vec3 vWp;
    void main(){
      vec4 c = texture2D(uMap, vUv);
      /* fake planet shadow across the rings */
      vec3 local = vWp - vec3(300.0, -18.0, -1780.0);
      vec3 away = local - uSunDir * dot(local, uSunDir);
      float shadow = smoothstep(34.0, 44.0, length(away));
      shadow = mix(shadow, 1.0, step(0.0, dot(local, uSunDir)));
      gl_FragColor = vec4(c.rgb * (0.25 + 0.85 * shadow), c.a * 0.96);
    }`,
});
const rings = new THREE.Mesh(ringGeo, ringMat);
rings.rotation.x = Math.PI / 2 - 0.42;
saturnGroup.add(rings);
saturnGroup.rotation.z = 0.18;

/* Uranus + Neptune, deep chapter markers */
addPlanet(new THREE.Mesh(
  new THREE.SphereGeometry(16, 72, 54),
  planetMaterial(T.uranus, { atmColor: 0x9fe8e2, atmStrength: 0.5, ambient: 0.04, terminator: 0.3 })
), new THREE.Vector3(-360, 90, -2320), 0.008);

addPlanet(new THREE.Mesh(
  new THREE.SphereGeometry(15, 72, 54),
  planetMaterial(T.neptune, { atmColor: 0x5f8cff, atmStrength: 0.65, ambient: 0.04, terminator: 0.32 })
), new THREE.Vector3(430, -70, -2650), 0.009);

/* ───────────────────────────── sun glow ───────────────────────────── */

const sunSprite = new THREE.Sprite(new THREE.SpriteMaterial({
  map: makeGlowTexture('#fff3d6'), transparent: true, opacity: 0.9,
  blending: THREE.AdditiveBlending, depthWrite: false,
}));
sunSprite.position.copy(SUN_POS);
sunSprite.scale.setScalar(2600);
scene.add(sunSprite);

/* ───────────────────────────── asteroid belt ───────────────────────────── */

const beltCount = isMobile ? 220 : 520;
const belt = new THREE.InstancedMesh(
  new THREE.DodecahedronGeometry(1, 0),
  new THREE.MeshStandardMaterial({ color: 0x38322c, roughness: 0.95, metalness: 0.05 }),
  beltCount
);
{
  const m = new THREE.Matrix4(), q = new THREE.Quaternion(), e = new THREE.Euler(), s = new THREE.Vector3();
  for (let i = 0; i < beltCount; i++) {
    const z = lerp(-560, -880, Math.random());
    const x = (Math.random() - 0.5) * 700;
    const y = (Math.random() - 0.5) * 260;
    e.set(Math.random() * 6.28, Math.random() * 6.28, Math.random() * 6.28);
    q.setFromEuler(e);
    const k = lerp(0.35, 2.6, Math.pow(Math.random(), 2.2));
    s.set(k, k * lerp(0.6, 1.3, Math.random()), k * lerp(0.6, 1.3, Math.random()));
    m.compose(new THREE.Vector3(x, y, z), q, s);
    belt.setMatrixAt(i, m);
  }
}
scene.add(belt);

/* ───────────────────────────── nebula ───────────────────────────── */

const nebulaShader = (tintA, tintB, seed) => new THREE.ShaderMaterial({
  transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
  uniforms: {
    uTime: { value: 0 }, uGain: { value: 0 },
    uA: { value: new THREE.Color(tintA) }, uB: { value: new THREE.Color(tintB) },
    uSeed: { value: seed },
  },
  vertexShader: /* glsl */`
    varying vec2 vUv;
    void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
  fragmentShader: /* glsl */`
    uniform float uTime, uGain, uSeed;
    uniform vec3 uA, uB;
    varying vec2 vUv;
    float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7)) + uSeed) * 43758.5453); }
    float noise(vec2 p){
      vec2 i = floor(p), f = fract(p);
      f = f * f * (3.0 - 2.0 * f);
      return mix(mix(hash(i), hash(i + vec2(1, 0)), f.x),
                 mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), f.x), f.y);
    }
    float fbm(vec2 p){
      float v = 0.0, a = 0.5;
      for(int i = 0; i < 5; i++){ v += a * noise(p); p *= 2.03; a *= 0.55; }
      return v;
    }
    void main(){
      vec2 p = vUv * 3.0;
      vec2 warp = vec2(fbm(p + uTime * 0.015), fbm(p + 5.2 - uTime * 0.011));
      float n = fbm(p + warp * 1.6);
      float body = smoothstep(0.42, 0.85, n);
      vec3 col = mix(uA, uB, smoothstep(0.45, 0.9, fbm(p * 0.6 + warp)));
      float edge = smoothstep(0.5, 0.12, length(vUv - 0.5));
      gl_FragColor = vec4(col * body * 1.4, body * edge * uGain);
    }`,
});
const nebulae = [];
[
  { pos: [-420, 140, -2480], rot: 0.4, size: [1700, 1050], a: 0x11295e, b: 0x1e6f86, seed: 3.1 },
  { pos: [380, -120, -2640], rot: -0.5, size: [1500, 950], a: 0x241a4e, b: 0x64303e, seed: 8.7 },
  { pos: [-80, 40, -2860], rot: 0.15, size: [2100, 1300], a: 0x0d1f4a, b: 0x2b6a8f, seed: 15.3 },
].forEach(({ pos, rot, size, a, b, seed }) => {
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(size[0], size[1]), nebulaShader(a, b, seed));
  mesh.position.set(...pos);
  mesh.rotation.z = rot;
  scene.add(mesh);
  nebulae.push(mesh);
});

/* ───────────────────────────── near dust ───────────────────────────── */

const DUST_BOX = 140;
const dustCount = isMobile ? 320 : 760;
const dust = (() => {
  const pos = new Float32Array(dustCount * 3);
  for (let i = 0; i < dustCount * 3; i++) pos[i] = (Math.random() - 0.5) * DUST_BOX;
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  const m = new THREE.ShaderMaterial({
    transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    uniforms: { uCam: { value: new THREE.Vector3() }, uPx: { value: 1 } },
    vertexShader: /* glsl */`
      uniform vec3 uCam; uniform float uPx;
      varying float vA;
      void main(){
        vec3 p = mod(position - uCam + ${DUST_BOX / 2}.0, ${DUST_BOX}.0) - ${DUST_BOX / 2}.0 + uCam;
        vec4 mv = modelViewMatrix * vec4(p, 1.0);
        float d = -mv.z;
        vA = smoothstep(${DUST_BOX / 2}.0, 12.0, d) * 0.5;
        gl_PointSize = uPx * 130.0 / d;
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: /* glsl */`
      varying float vA;
      void main(){
        float d = length(gl_PointCoord - 0.5);
        gl_FragColor = vec4(vec3(0.65, 0.75, 1.0), smoothstep(0.5, 0.1, d) * vA);
      }`,
  });
  return new THREE.Points(g, m);
})();
scene.add(dust);

/* ───────────────────────────── shooting stars ───────────────────────────── */

const streaks = [];
for (let i = 0; i < 3; i++) {
  const g = new THREE.PlaneGeometry(26, 0.22);
  const m = new THREE.MeshBasicMaterial({
    map: makeStreakTexture(), transparent: true, opacity: 0,
    blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(g, m);
  scene.add(mesh);
  streaks.push({ mesh, t: -1, next: 3 + i * 4 });
}
function makeStreakTexture() {
  const cv = document.createElement('canvas');
  cv.width = 256; cv.height = 8;
  const ctx = cv.getContext('2d');
  const g = ctx.createLinearGradient(0, 0, 256, 0);
  g.addColorStop(0, 'transparent');
  g.addColorStop(0.75, 'rgba(200,220,255,0.9)');
  g.addColorStop(1, 'rgba(255,255,255,1)');
  ctx.fillStyle = g; ctx.fillRect(0, 0, 256, 8);
  return new THREE.CanvasTexture(cv);
}

/* ───────────────────────────── post ───────────────────────────── */

const composer = new EffectComposer(renderer);
composer.addPass(new RenderPass(scene, camera));
const bloom = new UnrealBloomPass(new THREE.Vector2(innerWidth, innerHeight), 0.55, 0.85, 0.82);
composer.addPass(bloom);
composer.addPass(new OutputPass());
const filmPass = new ShaderPass({
  uniforms: {
    tDiffuse: { value: null },
    uTime: { value: 0 },
    uVig: { value: 0.55 },
    uGrain: { value: 0.028 },
  },
  vertexShader: /* glsl */`
    varying vec2 vUv;
    void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
  fragmentShader: /* glsl */`
    uniform sampler2D tDiffuse; uniform float uTime, uVig, uGrain;
    varying vec2 vUv;
    float hash(vec2 p){ return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453 + uTime); }
    void main(){
      vec2 d = vUv - 0.5;
      float r2 = dot(d, d);
      vec2 ca = d * r2 * 0.005;
      vec3 col;
      col.r = texture2D(tDiffuse, vUv - ca).r;
      col.g = texture2D(tDiffuse, vUv).g;
      col.b = texture2D(tDiffuse, vUv + ca).b;
      col *= 1.0 - r2 * uVig * 1.6;
      col += (hash(vUv * 900.0) - 0.5) * uGrain;
      gl_FragColor = vec4(col, 1.0);
    }`,
});
composer.addPass(filmPass);

/* ───────────────────────────── camera rail ───────────────────────────── */

/*
  key: [p, camX, camY, camZ, lookX, lookY, lookZ, fov]
  The rail flows outward along -Z past every world, then turns to face home.
*/
const KEYS = [
  [0.000,    0,    3,  118,     0,   0,    0, 62],
  [0.060,    0,    2,   62,     0,   0,    0, 60],
  [0.130,  -17,    5,   30,     0,   0,    0, 55],
  [0.200,   21,    7,   30,     4,   0,    0, 52],
  [0.260,   38,    4,  -30,    18,   0, -160, 50],
  [0.330,   26,    0, -140,     0,  -4, -320, 52],
  [0.420,   68,    2, -352,   104, -16, -442, 50],
  [0.500,   50,  -12, -498,   100, -14, -438, 52],
  [0.580, -160,   34, -905,  -275,  26, -1080, 50],
  [0.660, -110,   20, -1150, -240,  20, -1330, 56],
  [0.740,  128,   14, -1452,  318, -46, -1788, 47],
  [0.820,   60,   30, -2050,  -80,  60, -2500, 62],
  [0.880,  -40,   50, -2320,   40,   0, -2900, 68],
  [0.935,    0,   60, -2560,     0,  30, -3100, 64],
  [0.975,   10,   64, -2650,     0,   4,    0, 56],
  [1.000,    0,   58, -2680,     0,   0,    0, 54],
];
const camCurve = new THREE.CatmullRomCurve3(KEYS.map(k => new THREE.Vector3(k[1], k[2], k[3])), false, 'centripetal');
const lookCurve = new THREE.CatmullRomCurve3(KEYS.map(k => new THREE.Vector3(k[4], k[5], k[6])), false, 'centripetal');

/* map raw p → curve parameter honoring key spacing */
function keyT(p) {
  for (let i = 1; i < KEYS.length; i++) {
    if (p <= KEYS[i][0]) {
      const a = KEYS[i - 1][0], b = KEYS[i][0];
      return (i - 1 + (p - a) / (b - a)) / (KEYS.length - 1);
    }
  }
  return 1;
}
function fovAt(p) {
  for (let i = 1; i < KEYS.length; i++) {
    if (p <= KEYS[i][0]) {
      const a = KEYS[i - 1], b = KEYS[i];
      return lerp(a[7], b[7], smoothstep(a[0], b[0], p));
    }
  }
  return KEYS[KEYS.length - 1][7];
}

/* ───────────────────────────── scroll + chapters ───────────────────────────── */

let rawP = 0, p = 0;
/* scroll height only changes on resize (scroll-space is vh-sized), so cache it
   instead of forcing a layout read inside every scroll event */
let docH = 1;
const measureDoc = () => { docH = document.documentElement.scrollHeight - innerHeight; };
measureDoc();
const onScroll = () => {
  rawP = docH > 0 ? clamp(scrollY / docH, 0, 1) : 0;
};
addEventListener('scroll', onScroll, { passive: true });
addEventListener('load', () => { measureDoc(); onScroll(); });
onScroll();

const chapters = [...root.querySelectorAll('[data-chapter]')].map(el => ({
  el,
  a: parseFloat(el.dataset.start),
  b: parseFloat(el.dataset.end),
  hold: 'hold' in el.dataset,
  live: false,
}));
const navDots = [...document.querySelectorAll('.cosmos-nav__dot')];
navDots.forEach(dot => {
  dot.addEventListener('click', () => {
    smoothScrollTo(parseFloat(dot.dataset.p) * docH);
  });
});
let scrollAnim = null;
function smoothScrollTo(y) {
  cancelAnimationFrame(scrollAnim);
  if (reducedMotion) { scrollTo(0, y); return; }
  const from = scrollY, dist = y - from, dur = clamp(Math.abs(dist) / 4000, 0.6, 2.6) * 1000;
  const t0 = performance.now();
  const step = now => {
    const t = clamp((now - t0) / dur, 0, 1);
    const e = 1 - Math.pow(1 - t, 3);
    scrollTo(0, from + dist * e);
    if (t < 1) scrollAnim = requestAnimationFrame(step);
  };
  scrollAnim = requestAnimationFrame(step);
}
['wheel', 'touchstart'].forEach(ev =>
  addEventListener(ev, () => cancelAnimationFrame(scrollAnim), { passive: true })
);
document.querySelectorAll('[data-scroll-to]').forEach(el => {
  el.addEventListener('click', () => {
    smoothScrollTo(parseFloat(el.dataset.scrollTo) * docH);
  });
});

const hint = root.querySelector('.cosmos-hint');
const progressLine = document.querySelector('.cosmos-progress i');

/* mouse parallax + cursor */
const mouse = { x: 0, y: 0, sx: 0, sy: 0 };
addEventListener('pointermove', e => {
  mouse.x = (e.clientX / innerWidth) * 2 - 1;
  mouse.y = (e.clientY / innerHeight) * 2 - 1;
}, { passive: true });

const cursorDot = document.querySelector('.cosmos-cursor__dot');
const cursorHalo = document.querySelector('.cosmos-cursor__halo');
const cursor = { x: innerWidth / 2, y: innerHeight / 2, hx: innerWidth / 2, hy: innerHeight / 2, scale: 1 };
if (finePointer && cursorDot) {
  document.documentElement.classList.add('has-cosmos-cursor');
  addEventListener('pointermove', e => { cursor.x = e.clientX; cursor.y = e.clientY; }, { passive: true });
  document.querySelectorAll('a, button, .cosmos-nav__dot').forEach(el => {
    el.addEventListener('pointerenter', () => { cursor.scale = 2.6; });
    el.addEventListener('pointerleave', () => { cursor.scale = 1; });
  });
}

/* magnetic buttons */
if (finePointer) {
  document.querySelectorAll('.cosmos-btn').forEach(btn => {
    btn.addEventListener('pointermove', e => {
      const r = btn.getBoundingClientRect();
      const dx = e.clientX - (r.left + r.width / 2);
      const dy = e.clientY - (r.top + r.height / 2);
      btn.style.transform = `translate(${dx * 0.22}px, ${dy * 0.22}px)`;
    });
    btn.addEventListener('pointerleave', () => { btn.style.transform = ''; });
  });
}

/* split headlines into animated words */
document.querySelectorAll('[data-split]').forEach(el => {
  const words = el.textContent.trim().split(/\s+/);
  el.textContent = '';
  el.setAttribute('aria-label', words.join(' '));
  words.forEach((w, i) => {
    const span = document.createElement('span');
    span.className = 'w';
    span.style.transitionDelay = `${i * 70}ms`;
    span.textContent = w;
    span.setAttribute('aria-hidden', 'true');
    el.append(span, ' ');
  });
});

/* ───────────────────────────── adaptive quality ───────────────────────────── */

const DPR_TIERS = isMobile ? [1.5, 1.25, 1] : [Math.min(devicePixelRatio, 2), 1.5, 1.25, 1];
let tier = 0, frameEMA = 16, lastStep = 0;
function applyTier() {
  const dpr = DPR_TIERS[tier];
  renderer.setPixelRatio(dpr);
  composer.setPixelRatio(dpr);
  renderer.setSize(innerWidth, innerHeight);
  composer.setSize(innerWidth, innerHeight);
  stars.material.uniforms.uPx.value = dpr;
  dust.material.uniforms.uPx.value = dpr;
}
applyTier();

addEventListener('resize', () => {
  camera.aspect = innerWidth / innerHeight;
  camera.updateProjectionMatrix();
  applyTier();
  measureDoc();
  onScroll();
});

/* ───────────────────────────── main loop ───────────────────────────── */

const clock = new THREE.Clock();
let started = false;
const tmpPos = new THREE.Vector3(), tmpLook = new THREE.Vector3(), tmpDir = new THREE.Vector3();

function frame() {
  requestAnimationFrame(frame);
  if (document.hidden) return; // don't burn GPU in background tabs
  const dt = Math.min(clock.getDelta(), 0.05);
  const t = clock.elapsedTime;

  /* damped progress */
  const damp = reducedMotion ? 1 : 1 - Math.exp(-dt * 5.2);
  p = lerp(p, rawP, damp);
  if (Math.abs(p - rawP) < 0.0004) p = rawP;

  /* frame-time watchdog */
  frameEMA = lerp(frameEMA, dt * 1000, 0.04);
  if (frameEMA > 26 && tier < DPR_TIERS.length - 1 && t - lastStep > 3 && started) {
    tier++; lastStep = t; applyTier();
    if (tier === DPR_TIERS.length - 1) bloom.enabled = false;
  }

  /* camera on rail */
  const kt = keyT(p);
  camCurve.getPoint(kt, tmpPos);
  lookCurve.getPoint(kt, tmpLook);

  mouse.sx = lerp(mouse.sx, mouse.x, 1 - Math.exp(-dt * 3));
  mouse.sy = lerp(mouse.sy, mouse.y, 1 - Math.exp(-dt * 3));
  const drift = reducedMotion ? 0 : 1;
  tmpPos.x += mouse.sx * 1.6 * drift + Math.sin(t * 0.11) * 0.6 * drift;
  tmpPos.y += -mouse.sy * 1.1 * drift + Math.cos(t * 0.14) * 0.45 * drift;

  camera.position.copy(tmpPos);
  tmpLook.x += mouse.sx * 2.2 * drift;
  tmpLook.y += -mouse.sy * 1.4 * drift;
  camera.lookAt(tmpLook);
  camera.fov = fovAt(p);
  camera.updateProjectionMatrix();

  /* worlds alive */
  earth.rotation.y = t * 0.0155 + p * 2.2;
  clouds.rotation.y = t * 0.021 + p * 2.2;
  earthMat.uniforms.uCloudShift.value = (clouds.rotation.y - earth.rotation.y) / (Math.PI * 2);
  moonPivot.rotation.y = t * 0.02 + 1.1 + p * 3.0;
  moon.rotation.y = t * 0.02;
  planets.forEach(({ mesh, spin }) => { mesh.rotation.y += spin * dt; });
  jupiterMat.uniforms.uTime.value = t;
  belt.rotation.z = t * 0.004;

  /* starfield + dust */
  stars.material.uniforms.uTime.value = t;
  stars.material.uniforms.uFade.value = started ? smoothstep(0, 0.035, p + 0.02) : 0;
  dust.material.uniforms.uCam.value.copy(camera.position);

  /* nebulae breathe in for their chapter */
  const nebGain = envelope(p, 0.78, 0.95, 0.3, 0.3) * 0.9 + 0.06;
  nebulae.forEach((n, i) => {
    n.material.uniforms.uTime.value = t + i * 40;
    n.material.uniforms.uGain.value = nebGain;
  });

  /* pale blue dot beacon */
  dotGlow.material.opacity = envelope(p, 0.93, 1.001, 0.4, 0.9) * 0.85;

  /* shooting stars */
  streaks.forEach(s => {
    if (s.t < 0) {
      s.next -= dt;
      if (s.next <= 0 && started) {
        s.t = 0;
        camera.getWorldDirection(tmpDir);
        s.mesh.position.copy(camera.position)
          .addScaledVector(tmpDir, 90)
          .add(new THREE.Vector3((Math.random() - 0.5) * 90, (Math.random() - 0.3) * 55, (Math.random() - 0.5) * 40));
        s.mesh.rotation.z = -0.4 - Math.random() * 0.7;
        s.mesh.lookAt(camera.position);
        s.mesh.rotateZ(-0.5 - Math.random() * 0.9);
        s.vel = new THREE.Vector3(-1, -0.55, 0).applyEuler(s.mesh.rotation).multiplyScalar(90 + Math.random() * 60);
      }
    } else {
      s.t += dt;
      s.mesh.position.addScaledVector(s.vel, dt);
      s.mesh.material.opacity = Math.sin(clamp(s.t / 1.1, 0, 1) * Math.PI) * 0.8;
      if (s.t > 1.1) { s.t = -1; s.next = 4 + Math.random() * 7; s.mesh.material.opacity = 0; }
    }
  });

  /* exposure + bloom breathing per act */
  renderer.toneMappingExposure = lerp(0.92, 1.18, envelope(p, 0.0, 0.2, 0.5, 0.3) + envelope(p, 0.9, 1.001, 0.5, 0.9) * 0.5);
  bloom.strength = 0.45 + envelope(p, 0.78, 0.95, 0.3, 0.3) * 0.5 + envelope(p, 0, 0.12, 0.5, 0.4) * 0.2;
  filmPass.uniforms.uTime.value = t % 10;

  /* DOM chapters — quantize + cache every style so idle frames write nothing,
     and skip blur on mobile (filter repaints on top of WebGL are the main
     scroll-jank source on weak GPUs) */
  chapters.forEach(ch => {
    let e;
    if (ch.hold) e = smoothstep(ch.a, ch.a + (ch.b - ch.a) * 0.35, p);
    else if (ch.a <= 0.001) e = started ? 1 - smoothstep(ch.b - (ch.b - ch.a) * 0.3, ch.b, p) : 0;
    else e = envelope(p, ch.a, ch.b);
    if (e <= 0.001) {
      if (ch.el.style.visibility !== 'hidden') {
        ch.el.style.visibility = 'hidden';
        ch.op = ch.ty = ch.bl = ch.pe = undefined;
      }
      if (ch.live && e === 0) { ch.live = false; ch.el.classList.remove('is-live'); }
      return;
    }
    ch.el.style.visibility = 'visible';
    const op = Math.round(e * 200) / 200;
    if (op !== ch.op) { ch.op = op; ch.el.style.opacity = op; }
    const local = (p - ch.a) / (ch.b - ch.a);
    const ty = Math.round((0.5 - local) * 60 * 2) / 2;
    if (ty !== ch.ty) { ch.ty = ty; ch.el.style.transform = `translateY(${ty}px)`; }
    const bl = (isMobile || e >= 0.98) ? 0 : Math.round((1 - e) * 10 * 2) / 2;
    if (bl !== ch.bl) { ch.bl = bl; ch.el.style.filter = bl ? `blur(${bl}px)` : 'none'; }
    const pe = e > 0.45 ? 'auto' : 'none';
    if (pe !== ch.pe) { ch.pe = pe; ch.el.style.pointerEvents = pe; }
    if (!ch.live && e > 0.12) { ch.live = true; ch.el.classList.add('is-live'); }
    else if (ch.live && e < 0.03) { ch.live = false; ch.el.classList.remove('is-live'); }
  });

  /* hint + progress + nav */
  if (hint) hint.style.opacity = clamp(1 - p * 22, 0, 1) * (started ? 1 : 0);
  if (progressLine) progressLine.style.transform = `scaleY(${p})`;
  navDots.forEach(d => {
    const dp = parseFloat(d.dataset.p);
    d.classList.toggle('is-active', Math.abs(dp - p) < 0.055);
  });

  /* cursor */
  if (finePointer && cursorDot) {
    cursor.hx = lerp(cursor.hx, cursor.x, 1 - Math.exp(-dt * 10));
    cursor.hy = lerp(cursor.hy, cursor.y, 1 - Math.exp(-dt * 10));
    cursorDot.style.transform = `translate(${cursor.x}px, ${cursor.y}px)`;
    cursorHalo.style.transform = `translate(${cursor.hx}px, ${cursor.hy}px) scale(${cursor.scale})`;
  }

  composer.render();
}

/* ───────────────────────────── start ───────────────────────────── */

manager.onLoad = () => {
  /* warm the pipeline before revealing */
  renderer.compile(scene, camera);
  composer.render();
  setTimeout(() => {
    started = true;
    document.documentElement.classList.add('cosmos-ready');
    if (preloadEl) {
      preloadEl.classList.add('is-done');
      setTimeout(() => preloadEl.remove(), 1600);
    }
  }, 350);
};
manager.onError = url => console.warn('cosmos: failed to load', url);

/* debug handle for automated verification */
window.__cosmosDebug = { jump(v) { rawP = v; p = v; }, get p() { return p; } };

frame();
